import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  signInWithPopup,
  GoogleAuthProvider,
  UserCredential,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';
import {
  clearUserScopedBrowserState,
  waitForUserScopedBrowserStateTransitions,
} from '../services/userScopedCache';
import { mediaUrlService } from '../services/mediaUrlService';
import { processingRealtimeStore } from '../services/processingRealtimeStore';
import { uploadQueue } from '../services/uploadQueue';
import {
  AUTH_BOUNDARY_UNSETTLED_ERROR_CODE,
  activateProtectedTransportIdentity,
  beginProtectedTransportIdentityTransition,
  createAuthBoundaryUnsettledError,
  deactivateProtectedTransportBoundary,
} from '../services/protectedTransportBoundary';
import { buildAuthActionCodeSettings } from '../utils/firebaseAuthActions';
import { shouldRequireEmailVerification } from '../utils/authGuards';
import { apiService } from '../services/api';
import type { InteractiveLoginEvent } from '../services/processingActivity';
import {
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from '../utils/safeStorage';

interface AuthContextType {
  currentUser: User | null;
  signInProvider: string | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  loading: boolean;
  authTransitionError: string | null;
  interactiveLoginEvent: InteractiveLoginEvent | null;
  acknowledgeInteractiveLogin: (sequence: number) => void;
  retryAuthTransition: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  sendVerificationEmail: (idempotencyKey?: string) => Promise<void>;
  refreshVerificationStatus: () => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AUTH_BOUNDARY_UNSETTLED_ERROR_CODE };

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
  onInteractiveLogin?: () => void;
  loadingFallback?: React.ReactNode;
}

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({ prompt: 'select_account' });

export function AuthProvider({ children, onInteractiveLogin, loadingFallback = null }: AuthProviderProps) {
  const isLocalhost =
    typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const isE2EMockAuth = process.env.REACT_APP_E2E_MOCK_AUTH === 'true' && isLocalhost;

  // In E2E mock auth mode we bypass Firebase initialization entirely. We still need
  // a stable "authenticated" user object so the app can render tabs/screens for
  // screenshot tests without requiring real credentials.
  const buildE2EUser = (): User =>
    ({
      uid: 'e2e-user',
      email: 'e2e.user@example.com',
      emailVerified: true,
      getIdToken: async () => 'e2e-token',
    } as any as User);

  const [currentUser, setCurrentUser] = useState<User | null>(isE2EMockAuth ? buildE2EUser() : null);
  const [signInProvider, setSignInProvider] = useState<string | null>(isE2EMockAuth ? 'google.com' : null);
  const [loading, setLoading] = useState(!isE2EMockAuth);
  const [authTransitionError, setAuthTransitionError] = useState<string | null>(null);
  const [interactiveLoginEvent, setInteractiveLoginEvent] = useState<InteractiveLoginEvent | null>(null);
  const interactiveLoginSequenceRef = useRef(0);
  const [, setAuthTick] = useState(0);
  const authEventGenerationRef = useRef(0);
  const authBoundaryGenerationRef = useRef(0);
  const authTransitioningRef = useRef(!isE2EMockAuth);
  const pendingScopeTransitionsRef = useRef(0);
  const hasCommittedAuthEventRef = useRef(isE2EMockAuth);
  const interactiveLoginPendingRef = useRef(false);
  const onInteractiveLoginRef = useRef(onInteractiveLogin);
  onInteractiveLoginRef.current = onInteractiveLogin;
  const verificationRefreshRef = useRef<Promise<boolean> | null>(null);
  const verificationSendRef = useRef<Promise<void> | null>(null);
  const retryAuthTransitionRef = useRef<() => Promise<void>>(async () => {});
  const committedUserIdRef = useRef<string | null>(
    isE2EMockAuth
      ? 'e2e-user'
      : safeLocalStorageGet('currentUserId'),
  );

  const authActionCodeSettings = buildAuthActionCodeSettings();

  const sendVerificationEmailForCurrentUser = useCallback(async (idempotencyKey?: string) => {
    if (isE2EMockAuth) {
      throw new Error('Email verification is disabled in E2E mock auth mode');
    }
    if (!auth.currentUser) {
      throw new Error('No authenticated user to verify');
    }
    if (verificationSendRef.current) return verificationSendRef.current;
    const request = apiService.requestEmailVerification(idempotencyKey || crypto.randomUUID());
    verificationSendRef.current = request;
    try {
      await request;
    } finally {
      if (verificationSendRef.current === request) verificationSendRef.current = null;
    }
  }, [isE2EMockAuth]);

  const refreshVerificationStatus = useCallback(async (): Promise<boolean> => {
    if (verificationRefreshRef.current) return verificationRefreshRef.current;
    const user = auth.currentUser;
    if (!user) return false;
    if (authTransitioningRef.current) throw createAuthBoundaryUnsettledError();
    const generation = authBoundaryGenerationRef.current;
    const request = (async () => {
      await user.reload();
      const token = await user.getIdTokenResult(true);
      if (auth.currentUser?.uid !== user.uid || generation !== authBoundaryGenerationRef.current) {
        throw createAuthBoundaryUnsettledError();
      }
      const provider = (token.claims?.firebase as { sign_in_provider?: string } | undefined)?.sign_in_provider;
      setCurrentUser(user);
      setSignInProvider(provider || null);
      setAuthTick((tick) => tick + 1);
      return !shouldRequireEmailVerification(user, provider);
    })();
    verificationRefreshRef.current = request;
    try {
      return await request;
    } finally {
      if (verificationRefreshRef.current === request) verificationRefreshRef.current = null;
    }
  }, []);

  async function requestPasswordReset(email: string) {
    if (isE2EMockAuth) {
      throw new Error('Password reset is disabled in E2E mock auth mode');
    }
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      throw new Error('Email is required');
    }
    await sendPasswordResetEmail(auth, normalizedEmail, authActionCodeSettings);
  }

  const beginInteractiveIdentityMutation = (isLogin = false): string | null => {
    if (authTransitioningRef.current) {
      // Firebase auth mutations are not cancellable. Serializing them avoids
      // an older completion reactivating an identity while a newer account
      // switch is still pending.
      throw createAuthBoundaryUnsettledError();
    }
    const previousUserId = auth.currentUser?.uid || null;
    // An account switch/logout intent is already an identity boundary. Fence
    // token acquisition and protected binary responses before Firebase starts
    // the network operation. This also covers signed-out -> signed-in: Firebase
    // may update currentUser before its observer has cleared and rebound all
    // user-scoped browser state.
    authBoundaryGenerationRef.current += 1;
    authTransitioningRef.current = true;
    interactiveLoginPendingRef.current = isLogin;
    beginProtectedTransportIdentityTransition(null);
    return previousUserId;
  };

  const restoreFailedIdentityMutation = (previousUserId: string | null): void => {
    interactiveLoginPendingRef.current = false;
    if ((auth.currentUser?.uid || null) !== previousUserId) {
      return;
    }
    // Firebase kept the original account state, so the failed interactive
    // mutation may safely restore that exact identity under a fresh generation.
    activateProtectedTransportIdentity(
      previousUserId,
      () => auth.currentUser?.uid || null,
    );
    authTransitioningRef.current = false;
  };

  async function signup(email: string, password: string) {
    if (isE2EMockAuth) {
      throw new Error('Signup is disabled in E2E mock auth mode');
    }
    const previousUserId = beginInteractiveIdentityMutation(true);
    let credential: UserCredential;
    try {
      credential = await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      restoreFailedIdentityMutation(previousUserId);
      throw error;
    }
    return credential;
  }

  async function login(email: string, password: string) {
    if (isE2EMockAuth) {
      throw new Error('Login is disabled in E2E mock auth mode');
    }
    const previousUserId = beginInteractiveIdentityMutation(true);
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      restoreFailedIdentityMutation(previousUserId);
      throw error;
    }
  }

  async function signInWithGoogle() {
    if (isE2EMockAuth) {
      throw new Error('Google sign-in is disabled in E2E mock auth mode');
    }
    const previousUserId = beginInteractiveIdentityMutation(true);
    try {
      return await signInWithPopup(auth, googleProvider);
    } catch (error) {
      restoreFailedIdentityMutation(previousUserId);
      throw error;
    }
  }

  async function logout() {
    if (isE2EMockAuth) {
      return;
    }
    const previousUserId = beginInteractiveIdentityMutation();
    try {
      await signOut(auth);
    } catch (error) {
      restoreFailedIdentityMutation(previousUserId);
      throw error;
    }
  }

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (isE2EMockAuth) {
      return 'e2e-token';
    }
    if (authTransitioningRef.current) {
      // A boundary transition is not an anonymous session. Reject locally so
      // no old subtree or pending transport can dispatch without credentials
      // while browser state is being reassigned to another identity.
      throw createAuthBoundaryUnsettledError();
    }
    const canonicalUser = auth.currentUser;
    if (!canonicalUser) {
      return null;
    }
    const requestBoundaryGeneration = authBoundaryGenerationRef.current;
    const requestUserId = canonicalUser.uid;
    try {
      const token = await canonicalUser.getIdToken();
      if (
        authTransitioningRef.current
        || requestBoundaryGeneration !== authBoundaryGenerationRef.current
        || auth.currentUser?.uid !== requestUserId
      ) {
        // The token belongs to a superseded identity boundary. Never turn the
        // stale completion into an anonymous request.
        throw createAuthBoundaryUnsettledError();
      }
      return token;
    } catch (error) {
      if (
        !error
        || typeof error !== 'object'
        || (error as { code?: unknown }).code !== AUTH_BOUNDARY_UNSETTLED_ERROR_CODE
      ) {
        console.error('Error getting ID token:', error);
      }
      // A signed-in user's token refresh failure is not equivalent to an
      // anonymous session. Propagate it so transports can retry locally
      // instead of downgrading a protected request to an unauthenticated one.
      throw error;
    }
  }, [isE2EMockAuth]);

  useEffect(() => {
    if (isE2EMockAuth) {
      activateProtectedTransportIdentity('e2e-user', () => 'e2e-user');
      setCurrentUser(buildE2EUser());
      setSignInProvider('google.com');
      setAuthTick((tick) => tick + 1);
      setLoading(false);
      return () => {
        deactivateProtectedTransportBoundary();
      };
    }

    // onIdTokenChanged fires on initial auth load and when claims change (ex: email verified),
    // ensuring gating pages update without requiring a full sign-out/sign-in cycle.
    const handleAuthEvent = async (user: User | null) => {
      const eventGeneration = ++authEventGenerationRef.current;
      const newUserId = user?.uid || null;
      const transitionRequired = (
        committedUserIdRef.current !== newUserId
        || pendingScopeTransitionsRef.current > 0
      );
      const isCurrentAuthEvent = () => (
        eventGeneration === authEventGenerationRef.current
        && (auth.currentUser?.uid || null) === newUserId
      );

      // Clear all user-specific caches if user changed
      let transition: Promise<void> = waitForUserScopedBrowserStateTransitions();
      if (transitionRequired) {
        setAuthTransitionError(null);
        verificationRefreshRef.current = null;
        verificationSendRef.current = null;
        authBoundaryGenerationRef.current += 1;
        beginProtectedTransportIdentityTransition(newUserId);
        // Hide and detach the previous account subtree before the first
        // asynchronous cache operation can yield to another browser event.
        setLoading(true);
        setCurrentUser(null);
        setSignInProvider(null);
        authTransitioningRef.current = true;
        console.log('User changed, clearing caches...');
        pendingScopeTransitionsRef.current += 1;
        transition = clearUserScopedBrowserState({
          preserveLocalStorageUserId: newUserId,
        })
          .finally(() => {
            pendingScopeTransitionsRef.current = Math.max(
              0,
              pendingScopeTransitionsRef.current - 1,
            );
          });
      }

      try {
        // Even a same-UID event must wait behind a previously queued identity
        // transition before it can publish claims or remount the application.
        await transition;
      } catch (error) {
        if (eventGeneration === authEventGenerationRef.current) {
          console.error('Failed to clear state during authentication transition:', error);
          // Neither the previous nor the newly authenticated identity is safe
          // to expose while browser-scoped state is only partially cleared.
          // Explicitly detach every runtime scope and render a recoverable
          // boundary instead of leaving the application in an infinite loader.
          committedUserIdRef.current = null;
          safeLocalStorageRemove('currentUserId');
          mediaUrlService.activateUserScope(null);
          processingRealtimeStore.activateUserScope(null);
          uploadQueue.activateUserScope(null);
          deactivateProtectedTransportBoundary();
          setCurrentUser(null);
          setSignInProvider(null);
          authTransitioningRef.current = true;
          setAuthTransitionError(
            'We could not safely switch accounts because browser state could not be cleared.',
          );
          setLoading(false);
        }
        return;
      }

      if (!isCurrentAuthEvent()) {
        return;
      }

      let nextSignInProvider: string | null = null;
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          if (!isCurrentAuthEvent()) {
            return;
          }
          const firebaseClaims = tokenResult.claims?.firebase as { sign_in_provider?: unknown } | undefined;
          const providerClaim = firebaseClaims?.sign_in_provider;
          nextSignInProvider = typeof providerClaim === 'string'
            ? providerClaim
            : null;
        } catch (error) {
          if (!isCurrentAuthEvent()) {
            return;
          }
          console.warn('Failed to resolve active sign-in provider claim:', error);
        }
      }
      if (!isCurrentAuthEvent()) {
        return;
      }
      if (transitionRequired) {
        committedUserIdRef.current = newUserId;
        if (newUserId) {
          safeLocalStorageSet('currentUserId', newUserId);
        } else {
          safeLocalStorageRemove('currentUserId');
        }
        mediaUrlService.activateUserScope(newUserId);
        processingRealtimeStore.activateUserScope(newUserId);
      } else if (!hasCommittedAuthEventRef.current) {
        mediaUrlService.activateUserScope(newUserId);
        processingRealtimeStore.activateUserScope(newUserId);
      }
      // Publish the protected transport scope before remounting authenticated
      // children. Same-UID claim refreshes are intentionally a no-op.
      activateProtectedTransportIdentity(
        newUserId,
        () => auth.currentUser?.uid || null,
      );
      uploadQueue.activateUserScope(newUserId);
      if (newUserId && interactiveLoginPendingRef.current) {
        interactiveLoginPendingRef.current = false;
        setInteractiveLoginEvent({ userId: newUserId, sequence: ++interactiveLoginSequenceRef.current });
        onInteractiveLoginRef.current?.();
      } else if (!newUserId) {
        setInteractiveLoginEvent(null);
      }
      setCurrentUser(user);
      setSignInProvider(nextSignInProvider);
      setAuthTick((tick) => tick + 1);
      hasCommittedAuthEventRef.current = true;
      authTransitioningRef.current = false;
      setAuthTransitionError(null);
      setLoading(false);
    };

    retryAuthTransitionRef.current = async () => {
      setAuthTransitionError(null);
      setLoading(true);
      await handleAuthEvent(auth.currentUser);
    };

    const unsubscribe = onIdTokenChanged(auth, handleAuthEvent);

    return () => {
      authEventGenerationRef.current += 1;
      authTransitioningRef.current = true;
      retryAuthTransitionRef.current = async () => {};
      deactivateProtectedTransportBoundary();
      unsubscribe();
    };
  }, [isE2EMockAuth]);

  const retryAuthTransition = useCallback(async (): Promise<void> => {
    await retryAuthTransitionRef.current();
  }, []);

  const acknowledgeInteractiveLogin = useCallback((sequence: number): void => {
    setInteractiveLoginEvent((event) => event?.sequence === sequence ? null : event);
  }, []);

  const value: AuthContextType = {
    currentUser,
    signInProvider,
    login,
    signup,
    signInWithGoogle,
    logout,
    loading,
    authTransitionError,
    interactiveLoginEvent,
    acknowledgeInteractiveLogin,
    retryAuthTransition,
    getIdToken,
    sendVerificationEmail: sendVerificationEmailForCurrentUser,
    refreshVerificationStatus,
    requestPasswordReset,
  };

  return (
    <AuthContext.Provider value={value}>
      {authTransitionError ? (
        <main role="alert" aria-live="assertive" data-testid="auth-transition-error">
          <h1>Account switch needs attention</h1>
          <p>{authTransitionError}</p>
          <button type="button" onClick={() => void retryAuthTransition()}>
            Retry safely
          </button>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </main>
      ) : (
        loading ? loadingFallback : children
      )}
    </AuthContext.Provider>
  );
}
